import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { CopyButton, copyToClipboard } from '@/lib/clipboard-utils'
import { formatFullName, formatPhoneNumber } from '@/lib/utils'
import { Mail, Pencil, Phone, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { SwapContactDialog } from '@/components/shared/swap-contact-dialog'
import { ContactSheet } from '@/components/contacts/contact-edit-sheet'
import { useContact } from '@/stores/contacts.store'
import type { Contact } from '@/gen/cortado/v1/contacts_pb'

interface ContactHeaderProps {
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  phoneNumber?: string | null
  actionDropdown?: ReactNode
  /** Optional: inquiry ID to enable contact swapping */
  inquiryId?: string
  /** Optional: current contact ID for swap functionality */
  currentContactId?: string
  /** Optional: full contact object for editing */
  contact?: Contact
  /** Explicit archive state for embedded lead/event contacts. */
  isArchived?: boolean
}

/**
 * Get initials from a name (up to 2 characters)
 */
function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.trim()?.[0]?.toUpperCase() || ''
  const last = lastName?.trim()?.[0]?.toUpperCase() || ''
  return first + last || '?'
}


export function ContactHeader({
  firstName,
  lastName,
  email,
  phoneNumber,
  actionDropdown,
  inquiryId,
  currentContactId,
  contact,
  isArchived = false,
}: ContactHeaderProps) {
  const [swapDialogOpen, setSwapDialogOpen] = useState(false)
  const [editSheetOpen, setEditSheetOpen] = useState(false)
  const [emailCopied, setEmailCopied] = useState(false)
  const [phoneCopied, setPhoneCopied] = useState(false)
  const { contact: fetchedContact } = useContact(editSheetOpen ? currentContactId : undefined)
  const sheetContact = fetchedContact ?? contact

  const fullName = formatFullName(firstName, lastName, 'Contact')
  const emailValue = email ?? ''
  const phoneValue = phoneNumber ? formatPhoneNumber(phoneNumber) : ''
  const initials = getInitials(firstName, lastName)

  const handleEmailCopy = () => {
    if (emailValue && copyToClipboard(emailValue)) {
      setEmailCopied(true)
      setTimeout(() => setEmailCopied(false), 2000)
    }
  }

  const handlePhoneCopy = () => {
    if (phoneValue && copyToClipboard(phoneValue)) {
      setPhoneCopied(true)
      setTimeout(() => setPhoneCopied(false), 2000)
    }
  }

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      {/* Left side: Avatar + Info */}
      <div className="flex items-start gap-4">
        {/* Large Avatar */}
        <Avatar className="h-14 w-14 text-xl font-semibold shadow-lg">
          <AvatarFallback className="bg-slate-800 text-xl font-semibold text-slate-50">
            {initials}
          </AvatarFallback>
        </Avatar>

        {/* Name and Contact Info */}
        <div className="flex-1 min-w-0">
          {/* Name with company and edit/swap buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {currentContactId ? (
              <Link
                to="/contacts/$id"
                params={{ id: currentContactId }}
                className="text-2xl font-bold tracking-tight hover:underline"
              >
                {fullName}
              </Link>
            ) : (
              <h1 className="text-2xl font-bold tracking-tight">{fullName}</h1>
            )}
            {(isArchived || !!contact?.archivedAt) && (
              <Badge variant="secondary">Contact archived</Badge>
            )}
            {contact?.companies && contact.companies.length > 0 && (
              <span className="text-base text-muted-foreground font-normal">
                ({contact.companies.map(c => c.name).join(', ')})
              </span>
            )}
            {currentContactId && contact && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => setEditSheetOpen(true)}
                    aria-label="Edit contact"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  Edit contact details
                </TooltipContent>
              </Tooltip>
            )}
            {inquiryId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => setSwapDialogOpen(true)}
                    aria-label="Change contact"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  Change the contact for this lead
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Contact details - stacked on mobile, inline on desktop */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 mt-1.5 text-sm text-muted-foreground">
            {/* Email */}
            <div className="flex min-w-0 items-center gap-2">
              <Mail className="h-4 w-4 flex-shrink-0" />
              {emailValue ? (
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="truncate cursor-pointer hover:text-foreground transition-colors"
                    onClick={handleEmailCopy}
                  >
                    {emailValue}
                  </span>
                  <CopyButton
                    value={emailValue}
                    ariaLabel="Copy email"
                    copied={emailCopied}
                    onCopy={handleEmailCopy}
                  />
                </div>
              ) : (
                <span className="truncate text-muted-foreground/60">No email</span>
              )}
            </div>

            {/* Phone */}
            <div className="flex min-w-0 items-center gap-2">
              <Phone className="h-4 w-4 flex-shrink-0" />
              {phoneValue ? (
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="truncate cursor-pointer hover:text-foreground transition-colors"
                    onClick={handlePhoneCopy}
                  >
                    {phoneValue}
                  </span>
                  <CopyButton
                    value={phoneValue}
                    ariaLabel="Copy phone number"
                    copied={phoneCopied}
                    onCopy={handlePhoneCopy}
                  />
                </div>
              ) : (
                <span className="truncate text-muted-foreground/60">No phone</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right side: Action dropdown */}
      {actionDropdown && (
        <div className="flex-shrink-0">
          {actionDropdown}
        </div>
      )}

      {/* Edit Contact Sheet */}
      {currentContactId && sheetContact && (
        <ContactSheet
          organizationId={sheetContact.organizationId}
          contact={sheetContact}
          open={editSheetOpen}
          onOpenChange={setEditSheetOpen}
        />
      )}

      {/* Swap Contact Dialog */}
      {inquiryId && (
        <SwapContactDialog
          inquiryId={inquiryId}
          currentContactId={currentContactId}
          open={swapDialogOpen}
          onOpenChange={setSwapDialogOpen}
        />
      )}
    </div>
  )
}
